import clsx from 'clsx';
import moment from 'moment';
import React, { useCallback, useRef } from 'react';

import CheckMark from 'images/check_mark.svg';
import chevronDown from 'images/chevron_down_icon.svg';

import { useCloseElOnOutsideClick } from '@hooks';
import { bookmarks_feed_path, logout_path, settings_path, subscriber_profile_path } from '@routes';
import { edit_user_team_settings_path, gift_landing_path, referrals_path } from '@routes';

import navLinkStyles from '../NavLinks/styles.module.scss';
import styles from './styles.module.scss';

const CompletionBar = ({
  profileCompletionPercentage,
}: {
  profileCompletionPercentage: number;
}) => {
  return (
    <div className={styles.percentageBars}>
      {[20, 40, 60, 80, 100].map((percentage) => {
        return (
          <span
            className={clsx(styles.percentageBar, {
              [styles.filled]: profileCompletionPercentage >= percentage,
            })}
            key={percentage}
          />
        );
      })}
    </div>
  );
};

const DAYS_TO_SHOW_NEXT_STEP = 90;

export default function AvatarDropdown({
  avatarUrl,
  profileCompletionPercentage,
  profileCompletionStatuses,
  username,
  dropdownShown,
  setDropdownShown,
  initialSubscribeDateStr,
}: {
  avatarUrl: string;
  dropdownShown: string;
  profileCompletionPercentage: number;
  profileCompletionStatuses: { text: string; isComplete: boolean; href: string | null }[];
  setDropdownShown: React.Dispatch<React.SetStateAction<string>>;
  username: string;
  initialSubscribeDateStr: string;
}) {
  const dropdownIdentifier = 'avatar';
  const isOpen = dropdownShown === dropdownIdentifier;
  const closeDropdown = useCallback(() => setDropdownShown(''), [setDropdownShown]);

  const toggleRef = useRef();
  const { ref: dropdownRef } = useCloseElOnOutsideClick({
    closeFn: closeDropdown,
    isOpen,
    toggleEl: toggleRef.current,
  });

  const toggleDropdown = () => {
    isOpen ? closeDropdown() : setDropdownShown(dropdownIdentifier);
  };

  const showNextStep =
    initialSubscribeDateStr &&
    moment()
      .subtract(DAYS_TO_SHOW_NEXT_STEP, 'days')
      .isSameOrAfter(moment(initialSubscribeDateStr));

  return (
    <div className={styles.AvatarDropdown} data-test="nav-avatar-dropdown">
      <div className={styles.avatarWrapper} onClick={toggleDropdown} ref={toggleRef}>
        <img className={styles.avatar} src={avatarUrl} alt="User avatar" width="40" height="40" />
        <img
          src={chevronDown}
          width="10"
          className={clsx({
            [`${styles.chevronUp}`]: isOpen,
            [`${styles.chevronDown}`]: !isOpen,
          })}
        />
      </div>
      <div
        className={clsx(styles.dropdown, {
          [styles.show]: isOpen,
        })}
        ref={dropdownRef}
      >
        <div className={styles.dropdownLinksWrapper}>
          <div className={clsx(navLinkStyles.navLink)}>
            <a href={subscriber_profile_path(username)}>My Profile</a>
          </div>
          <div className={clsx(navLinkStyles.navLink)}>
            <a href={bookmarks_feed_path()}>My Bookmarks</a>
          </div>
          <div className={clsx(navLinkStyles.navLink)}>
            <a href={settings_path()}>My Settings</a>
          </div>
          <div className={clsx(navLinkStyles.navLink)}>
            <a className="logout" href={logout_path()}>
              Sign out
            </a>
          </div>
        </div>
        <div className={styles.completeProfileWrapper}>
          {showNextStep ? (
            <>
              <div className={clsx(styles.subtitle, styles.subtitleNextStep)}>
                <span>The Next Step</span>
              </div>
              <a className={styles.profileItem} href={referrals_path()}>
                Refer a Friend
              </a>
              <a className={styles.profileItem} href={gift_landing_path()}>
                Give a Subscription
              </a>
              <a className={styles.profileItem} href={edit_user_team_settings_path()}>
                Setup a Team
              </a>
            </>
          ) : (
            <>
              <div className={styles.subtitle}>
                <span>Complete Your Profile</span>
                <span className={styles.profilePercentage}>{profileCompletionPercentage}%</span>
                <CompletionBar profileCompletionPercentage={profileCompletionPercentage} />
              </div>
              {profileCompletionStatuses.map((item) => (
                <a
                  className={clsx(styles.profileItem, {
                    [styles.completedProfileItem]: item.isComplete,
                  })}
                  href={!item.isComplete && item.href ? item.href : undefined}
                  key={item.text}
                >
                  <img className={styles.checkMark} src={CheckMark} width="20px" />
                  <span className={styles.text}>{item.text}</span>
                </a>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
